import {
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Box,
    Grid,
    LinearProgress,
    Paper,
    Typography,
} from "@mui/material";

import {
    Store,
} from "@material-ui/icons";

import {
    fetchAvailableOffersAPI,
} from "../api/offer_api";

import {
    handleResponse,
} from "../util/integration";

import {
    POLLING_SPEED,
    usePolling,
} from "../util/polling";

import {
    PRIMARY_COLOR,
} from "../util/constants";

import Offer from "../entity/Offer";


const LABEL = Object.freeze({
    title:
        "Angebote",
});


const MESSAGE = Object.freeze({
    noOffers:
        "Keine verfügbaren Angebote",

    pollingError:
        "Angebote konnten nicht aktualisiert werden.",

    newOffers: (
        count,
    ) => (
        count === 1
            ? "Ein neues Angebot ist verfügbar."
            : `${count} neue Angebote sind verfügbar.`
    ),
});


const CONFIG = Object.freeze({
    topSpacing:
        "50px",

    headerPadding:
        "25px",

    contentSpacing:
        7,

    pageSpacing:
        5,

    fullWidthColumns:
        12,

    offerColumns:
        4,
});


const STYLE = Object.freeze({
    topSpacing: {
        height:
            CONFIG.topSpacing,
    },

    titleContainer: {
        display:
            "flex",

        alignItems:
            "center",

        gap:
            1.5,
    },

    titleIcon: {
        color:
            "white",

        fontSize:
            30,
    },
});


const COMPONENT_PROPS = Object.freeze({
    pageGrid: {
        container:
            true,

        spacing:
            CONFIG.pageSpacing,
    },

    contentGrid: {
        container:
            true,

        spacing:
            CONFIG.contentSpacing,
    },

    fullWidthGridItem: {
        item:
            true,

        xs:
            CONFIG.fullWidthColumns,
    },

    offerGridItem: {
        item:
            true,

        xs:
            CONFIG.fullWidthColumns,

        md:
            CONFIG.offerColumns,
    },

    title: {
        variant:
            "h6",

        component:
            "h1",
    },
});


const createColorStyles = (
    primaryColor,
) => Object.freeze({
    header: {
        p:
            CONFIG.headerPadding,

        backgroundColor:
            primaryColor,

        color:
            "white",

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "space-between",

        gap:
            2,
    },
});


const COLOR_STYLE =
    createColorStyles(
        PRIMARY_COLOR,
    );


const normalizeOfferIds = (
    response,
) => {
    if (!Array.isArray(response)) {
        return [];
    }

    return response.filter(
        (offerId) => (
            offerId !== null
            && offerId !== undefined
        ),
    );
};


const getNewOfferCount = ({
    offerIds,
    previousSnapshot,
    userId,
}) => {
    if (
        previousSnapshot === null
        || !Object.is(
            previousSnapshot.userId,
            userId,
        )
    ) {
        return 0;
    }

    const previousOfferIds =
        new Set(
            previousSnapshot.offerIds,
        );

    const newOfferIds =
        new Set(
            offerIds.filter(
                (offerId) => (
                    !previousOfferIds.has(
                        offerId,
                    )
                ),
            ),
        );

    return newOfferIds.size;
};


const useOffers = ({
    accessToken,
    userId,
    showMessage,
}) => {
    const [
        offerList,
        setOfferList,
    ] = useState([]);

    const [
        isLoading,
        setIsLoading,
    ] = useState(true);

    const [
        reloadVersion,
        setReloadVersion,
    ] = useState(0);

    const previousOfferSnapshotRef =
        useRef(null);


    const pollingRestartKey =
        useMemo(
            () => ({
                accessToken,
                userId,
                reloadVersion,
            }),
            [
                accessToken,
                reloadVersion,
                userId,
            ],
        );


    const handleOfferResponse =
        useCallback(
            (
                response,
            ) => {
                const offerIds =
                    normalizeOfferIds(
                        response,
                    );

                const previousSnapshot =
                    previousOfferSnapshotRef
                        .current;

                const newOfferCount =
                    getNewOfferCount({
                        offerIds,
                        previousSnapshot,
                        userId,
                    });

                previousOfferSnapshotRef
                    .current = {
                        userId,
                        offerIds,
                    };

                setOfferList(
                    offerIds,
                );

                setIsLoading(
                    false,
                );

                if (newOfferCount > 0) {
                    showMessage(
                        MESSAGE.newOffers(
                            newOfferCount,
                        ),
                    );
                }
            },
            [
                showMessage,
                userId,
            ],
        );


    const pollOffers =
        useCallback(
            ({
                isPollingActive,
            }) => {
                return new Promise(
                    (
                        resolve,
                        reject,
                    ) => {
                        const handleSuccess = (
                            response,
                        ) => {
                            if (
                                !isPollingActive()
                            ) {
                                resolve();
                                return;
                            }

                            try {
                                handleOfferResponse(
                                    response,
                                );

                                resolve();
                            } catch (error) {
                                reject(error);
                            }
                        };


                        const handleFailure = (
                            message,
                        ) => {
                            if (
                                !isPollingActive()
                            ) {
                                resolve();
                                return;
                            }

                            try {
                                setIsLoading(
                                    false,
                                );

                                showMessage(
                                    message,
                                );

                                resolve();
                            } catch (error) {
                                reject(error);
                            }
                        };


                        try {
                            handleResponse(
                                fetchAvailableOffersAPI(
                                    accessToken,
                                    userId,
                                ),
                                handleSuccess,
                                handleFailure,
                            );
                        } catch (error) {
                            reject(error);
                        }
                    },
                );
            },
            [
                accessToken,
                handleOfferResponse,
                showMessage,
                userId,
            ],
        );


    const handlePollingError =
        useCallback(
            () => {
                setIsLoading(
                    false,
                );

                showMessage(
                    MESSAGE.pollingError,
                );
            },
            [
                showMessage,
            ],
        );


    usePolling({
        poll:
            pollOffers,

        enabled:
            true,

        immediate:
            true,

        speed:
            POLLING_SPEED.medium,

        onError:
            handlePollingError,

        restartKey:
            pollingRestartKey,
    });


    const reloadOffers =
        useCallback(
            () => {
                setIsLoading(
                    true,
                );

                setReloadVersion(
                    (
                        currentVersion,
                    ) => (
                        currentVersion + 1
                    ),
                );
            },
            [],
        );


    return {
        offerList,

        hasOffers:
            offerList.length > 0,

        isLoading,

        reloadOffers,
    };
};


export default function Offers({
    appAPI,
    contextAPI,
}) {
    const accessToken =
        contextAPI.password;

    const userId =
        contextAPI.id;

    const showMessage =
        appAPI.showMessage;


    const {
        offerList,
        hasOffers,
        isLoading,
        reloadOffers,
    } = useOffers({
        accessToken,
        userId,
        showMessage,
    });


    return (
        <Grid
            {...COMPONENT_PROPS.pageGrid}
        >
            <Grid
                {...COMPONENT_PROPS.fullWidthGridItem}
            >
                <Box
                    sx={STYLE.topSpacing}
                />
            </Grid>

            <Grid
                {...COMPONENT_PROPS.fullWidthGridItem}
            >
                <Grid
                    {...COMPONENT_PROPS.contentGrid}
                >
                    <Grid
                        {...COMPONENT_PROPS.fullWidthGridItem}
                    >
                        <Paper
                            sx={COLOR_STYLE.header}
                        >
                            <Box
                                sx={STYLE.titleContainer}
                            >
                                <Store
                                    sx={STYLE.titleIcon}
                                />

                                <Typography
                                    {...COMPONENT_PROPS.title}
                                >
                                    {LABEL.title}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {isLoading && (
                        <Grid
                            {...COMPONENT_PROPS.fullWidthGridItem}
                        >
                            <LinearProgress />
                        </Grid>
                    )}

                    {!isLoading
                        && hasOffers
                        && offerList.map(
                            (
                                offerId,
                            ) => (
                                <Grid
                                    {...COMPONENT_PROPS.offerGridItem}
                                    key={offerId}
                                >
                                    <Offer
                                        offerId={offerId}
                                        appAPI={appAPI}
                                        contextAPI={contextAPI}
                                        reload={reloadOffers}
                                    />
                                </Grid>
                            ),
                        )}

                    {!isLoading
                        && !hasOffers
                        && (
                            <Grid
                                {...COMPONENT_PROPS.offerGridItem}
                            >
                                <Typography>
                                    {MESSAGE.noOffers}
                                </Typography>
                            </Grid>
                        )}
                </Grid>
            </Grid>
        </Grid>
    );
}

